// No imports, because `validate-agent-instructions.mjs` loads this source to lint init's bytes.

export const KNOWLEDGE_SKILL_NAME = 'agent-qa-knowledge'

export const CODING_AGENTS_URL = 'https://vostride.com/docs/agent-qa/coding-agents'

export const WRITE_AGENT_INSTRUCTIONS_FLAG = '--write-agent-instructions'

export interface AgentQuestion {
  readonly question: string
  readonly cli: string
  readonly mcp: string
}

// One question per read, each answered by a verb and its tool; the run map first.
export const AGENT_QUESTION_MAP: readonly AgentQuestion[] = Object.freeze([
  {
    question: 'What happened in a run, and where is everything it recorded?',
    cli: 'agent-qa runs overview <runId>',
    mcp: 'agent_qa_get_run_overview',
  },
  { question: 'Which runs exist, newest first?', cli: 'agent-qa runs list', mcp: 'agent_qa_list_runs' },
  {
    question: 'One step\'s console, network, a11y, aria or variables',
    cli: 'agent-qa runs evidence <runId> --test <n> --step <n> --kind <kind>',
    mcp: 'agent_qa_get_run_evidence',
  },
  {
    question: 'What knowledge did a run use and learn?',
    cli: 'agent-qa runs knowledge <runId>',
    mcp: 'agent_qa_get_run_knowledge',
  },
  {
    question: 'Why did a step\'s brief say what it said?',
    cli: 'agent-qa knowledge brief-why <runId> --step <n>',
    mcp: 'agent_qa_explain_brief',
  },
  {
    question: 'One record, with its provenance',
    cli: 'agent-qa knowledge show <recordId>',
    mcp: 'agent_qa_show_knowledge_record',
  },
  {
    question: 'Which knowledge bundles exist?',
    cli: 'agent-qa knowledge bundles',
    mcp: 'agent_qa_list_knowledge_bundles',
  },
  {
    question: 'Records by product, kind or scope',
    cli: 'agent-qa knowledge inspect',
    mcp: 'agent_qa_inspect_knowledge_records',
  },
  {
    question: 'What changed since a build?',
    cli: 'agent-qa knowledge diff <build>',
    mcp: 'agent_qa_diff_knowledge_records',
  },
  {
    question: 'Which marks and approvals back a record, and would it promote?',
    cli: 'agent-qa knowledge gates <recordId>',
    mcp: 'agent_qa_list_knowledge_gates',
  },
].map((row) => Object.freeze(row)))

export function buildKnowledgeAgentGuide(): string {
  return [
    '# agent-qa knowledge — for coding agents',
    '',
    'Generated by agent-qa init when it creates the workspace.',
    '',
    'This folder holds what agent-qa learned from test runs and the QA policies people wrote.',
    'Read it through agent-qa\'s commands, never through SQLite or index files.',
    '',
    '## What\'s here',
    '- `memory/<product>/bundles/<id>/` — one learned record per bundle: `record.md` and its evidence.',
    '- `policies/` — QA rules people wrote; the runner applies them on every run.',
    '- `memory/index/`, `memory/queue/`, `.lease`, `.epoch` and any SQLite file are generated',
    '  runtime state. Never read or edit them.',
    '',
    'Answer questions with the commands below: trust, validity and supersession are worked out',
    'when you ask, so a raw `record.md` can be stale or superseded. Opening a bundle to look',
    'around is fine.',
    '',
    '## Questions → commands',
    '| Question | CLI (add `--json` for JSON) | MCP tool |',
    '|---|---|---|',
    ...AGENT_QUESTION_MAP.map((row) => `| ${row.question} | \`npx ${row.cli}\` | \`${row.mcp}\` |`),
    '',
    'With `--json` each verb prints what its MCP tool answers; '
      + '`npx agent-qa knowledge inspect` and',
    '`npx agent-qa knowledge diff <build>` (`<build>` is a build ordinal or tag) print only the '
      + '`records` and `changes` array of that answer.',
    '',
    'Test and step numbers are zero-based, as `runs overview` prints them. A screenshot:',
    '`npx agent-qa runs evidence <runId> --screenshot <sha> --out <file>`.',
    '',
    '## Provenance',
    'Every record names where it came from: the runs and steps that taught it and the outcomes',
    'its trust rests on. `knowledge show` returns them; cite the record id when you rely on one.',
    '',
    '## Changing knowledge',
    'Never edit files under agent-qa-knowledge/ yourself; knowledge changes only through agent-qa.',
    '`agent_qa_change_knowledge` accepts, rejects or forgets a record, and the change applies at once.',
    'Wrong knowledge is corrected by new feedback (a run mark) or by forgetting the record.',
    '',
    `The \`${KNOWLEDGE_SKILL_NAME}\` skill has the workflow, with the depth in its references/ folder;`,
    '`npx agent-qa skills` prints where it is.',
    `Connecting Claude Code or Codex: ${CODING_AGENTS_URL}`,
    '',
  ].join('\n')
}

// The root pointer's block body, written between the managed markers or printed with them.
export function buildAgentInstructionPointer(): readonly string[] {
  return Object.freeze([
    '## agent-qa',
    'This project is tested with agent-qa. Read its data through',
    'agent-qa\'s commands (CLI with --json, or the agent_qa_* MCP',
    'tools), never through .agent-qa/ files, SQLite or index files.',
    '- Runs: `npx agent-qa runs list`, then `npx agent-qa runs overview <runId>`',
    '- Knowledge: read agent-qa-knowledge/AGENTS.md first',
    `- Skills: \`npx agent-qa skills\` lists them; host setup: ${CODING_AGENTS_URL}`,
    '- Secrets: only the values in `workspace.secretsFile` are masked,',
    '  shown as `[secret:<name>]`; use temporary, limited test credentials',
  ])
}
